// src/components/ad-banner/ad-banner.tsx
import React2 from "react";
import { Experiment as Experiment2, ExperimentControl as ExperimentControl2, ExperimentVariant as ExperimentVariant2 } from "@shared-ui/experiment-context";
import { AdBlockerAware } from "@shared-ui/retail-sponsored-content-ad-block-detect-wrapper";

// src/components/utils/index.ts
var AdTypeName = {
  display: "meso-display-ad"
};
var uuidv4 = () => {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
  // eslint-disable-next-line no-bitwise
  (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
};
var formatDisplayAdButton = formatText => {
  return {
    __typename: "AdTransparencyButton",
    primary: formatText("sponsoredContent.transparency.adButton.text"),
    size: "small",
    accessibility: formatText("sponsoredContent.transparency.adButton.text"),
    disabled: false,
    icon: null,
    analyticsPayload: null
  };
};
var formatOverlaySkeleton = formatText => {
  return {
    __typename: "TransparencyOverlaySkeleton",
    title: formatText("sponsoredContent.transparency.overlaySkeleton.title"),
    error: {
      __typename: "TransparencyOverlayError",
      icon: {
        __typename: "Icon",
        id: "info",
        size: "medium"
      },
      description: formatText("sponsoredContent.transparency.overlaySkeleton.errorDescription"),
      button: {
        __typename: "UIPrimaryButton",
        primary: formatText("sponsoredContent.transparency.overlaySkeleton.retryButton.text")
      },
      allowedRetryAttempts: 3,
      exceededAttemptsMessage: formatText("sponsoredContent.transparency.overlaySkeleton.errorExceededAttemptsMessage")
    }
  };
};
var buildAdTransparencyFragment = formatText => {
  return {
    __typename: "AdTransparencyTrigger",
    adTransparencyId: uuidv4().toString(),
    adTransparencyButton: formatDisplayAdButton(formatText),
    adTransparencyOverlaySkeleton: formatOverlaySkeleton(formatText)
  };
};

// src/components/ad-banner/__private/ad-banner-transparency.tsx
import React, { useEffect, useState } from "react";
import { EGDSSpacing } from "@egds/react-core/spacing";
import { EGDSText } from "@egds/react-core/text";
import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { useLocalization } from "@shared-ui/localization-context";
import { Transparency, TransparencyTriggerType } from "@shared-ui/retail-sponsored-content-transparency";
var AdBannerTransparency = ({
  lineItemId
}) => {
  const {
    formatText
  } = useLocalization();
  const [transparencyFragment, setTransparencyFragment] = useState({});
  const [acceptableAdEnabled, setAcceptableAdEnabled] = useState(false);
  useEffect(() => {
    var _a;
    setTransparencyFragment(buildAdTransparencyFragment(formatText));
    setAcceptableAdEnabled(((_a = localStorage.getItem("eg_adblock")) == null ? void 0 : _a.split("|").includes("aaa=1")) || false);
  }, []);
  return /* @__PURE__ */React.createElement(Experiment, {
    name: "EU_Ad_Transparency_Feature_Gate"
  }, /* @__PURE__ */React.createElement(ExperimentControl, null, acceptableAdEnabled ? /* @__PURE__ */React.createElement(Experiment, {
    name: "IAB_Ad_Badge"
  }, /* @__PURE__ */React.createElement(ExperimentControl, null), /* @__PURE__ */React.createElement(ExperimentVariant, {
    bucket: 1
  }, /* @__PURE__ */React.createElement(EGDSSpacing, {
    margin: {
      blockstart: "two"
    }
  }, /* @__PURE__ */React.createElement(EGDSText, {
    theme: "standard",
    size: 200
  }, formatText("sponsoredContent.transparency.adButton.text"))))) : null), /* @__PURE__ */React.createElement(ExperimentVariant, {
    bucket: 1
  }, /* @__PURE__ */React.createElement(EGDSSpacing, {
    padding: {
      blockstart: "two"
    }
  }, /* @__PURE__ */React.createElement("div", null, /* @__PURE__ */React.createElement(Transparency, {
    type: TransparencyTriggerType.DISPLAY_AD,
    lineItemId,
    data: transparencyFragment
  })))));
};

// src/components/ad-banner/ad-banner.tsx
var AdBanner = ({
  type,
  uci,
  displayAdRef,
  className,
  adSwapEnabled,
  isEnabled
}) => {
  const [slotRendered, setSlotRendered] = React2.useState(false);
  const [slotMediaQueryEnabled, setSlotMediaQueryEnabled] = React2.useState(false);
  const [lineItemId, setLineItemId] = React2.useState("");
  React2.useEffect(() => {
    const getSlotInMediaQuery = event => {
      const {
        slot,
        enabled
      } = event.detail;
      if (slot !== uci) {
        return;
      }
      setSlotMediaQueryEnabled(enabled);
    };
    window.addEventListener("OnMediaQuery", getSlotInMediaQuery);
    return () => {
      window.removeEventListener("OnMediaQuery", getSlotInMediaQuery);
    };
  }, [uci]);
  React2.useEffect(() => {
    const displayAdEl = displayAdRef.current;
    const getAdData = event => {
      const {
        detail
      } = event;
      if (detail.code !== "AD-LOAD-PASS") {
        setLineItemId("");
        setSlotRendered(false);
        return;
      }
      setSlotRendered(true);
      setLineItemId(detail.lineItemId);
    };
    if (displayAdEl) {
      displayAdEl.addEventListener("mesoAd", getAdData);
    }
    return () => {
      if (displayAdEl) {
        displayAdEl.removeEventListener("mesoAd", getAdData);
      }
    };
  }, [displayAdRef]);
  const displayAd = (_type, _uci, _displayAdRef, _className, _adSwapEnabled = false, _isEnabled = false) => {
    return React2.createElement(AdTypeName[_type || "display"], {
      uci: _uci,
      slot: _uci,
      ref: _displayAdRef,
      class: _className,
      adSwapEnabled: _adSwapEnabled ? true : null,
      enabled: _isEnabled ? true : null
    });
  };
  const adTransparency = (_slotRendered, _slotMediaQueryEnabled, _lineItemId) => {
    if (_slotRendered && _slotMediaQueryEnabled && _lineItemId) {
      return /* @__PURE__ */React2.createElement(AdBannerTransparency, {
        lineItemId: _lineItemId
      });
    }
    return null;
  };
  return /* @__PURE__ */React2.createElement(AdBlockerAware, {
    adBlockExperiment: "IAB_Acceptable_Ads_Enablement"
  }, /* @__PURE__ */React2.createElement(Experiment2, {
    name: "Split_View_Side_by_side_BEXG_H",
    logExposure: false
  }, /* @__PURE__ */React2.createElement(ExperimentControl2, null, displayAd(type, uci, displayAdRef, className), adTransparency(slotRendered, slotMediaQueryEnabled, lineItemId)), /* @__PURE__ */React2.createElement(ExperimentVariant2, {
    bucket: 1
  }, adTransparency(slotRendered, slotMediaQueryEnabled, lineItemId), displayAd(type, uci, displayAdRef, className, adSwapEnabled, isEnabled)), /* @__PURE__ */React2.createElement(ExperimentVariant2, {
    bucket: 2
  }, adTransparency(slotRendered, slotMediaQueryEnabled, lineItemId), displayAd(type, uci, displayAdRef, className, adSwapEnabled, isEnabled))));
};
AdBanner.displayName = "sharedui-AdBanner";
export { AdBanner };